// @ts-nocheck
import {
  useQueryClient,
  useMutation,
  UseQueryResult,
  UseQueryOptions,
  useQuery,
} from 'react-query';
import { useRequestQuery } from '../useQueryRequest';
import useApiRequest from '../useRequest';
import { transformPagination, transformToCamelCase } from '@/utils';
import { useRequestPdf } from '../useRequestPdf';
import t from './types';

const commonInvalidateQueries = (queryClient) => {
  // Invalidate grants.
  queryClient.invalidateQueries(t.GRANTS);

  // Invalidate accounts.
  queryClient.invalidateQueries(t.ITEMS);
  queryClient.invalidateQueries(t.ITEM);

  // Invalidate accounts.
  queryClient.invalidateQueries(t.ACCOUNTS);
  queryClient.invalidateQueries(t.ACCOUNT);

  // Invalidate financial reports.
  queryClient.invalidateQueries(t.FINANCIAL_REPORT);

  // Invalidate the transactions by reference.
  queryClient.invalidateQueries(t.TRANSACTIONS_BY_REFERENCE);

  // Invalidate the cashflow transactions.
  queryClient.invalidateQueries(t.CASH_FLOW_TRANSACTIONS);
  queryClient.invalidateQueries(t.CASHFLOW_ACCOUNT_TRANSACTIONS_INFINITY);

  // Invalidate
  queryClient.invalidateQueries(t.ITEM_ASSOCIATED_WITH_GRANTS);

  // Invalidate item warehouses.
  queryClient.invalidateQueries(t.ITEM_WAREHOUSES_LOCATION);

  // Invalidate the settings.
  queryClient.invalidateQueries([t.SETTING, t.SETTING_GRANTS]);

  // Invalidate mutate base currency abilities.
  queryClient.invalidateQueries(t.ORGANIZATION_MUTATE_BASE_CURRENCY_ABILITIES);
};

/**
 * Creates a new sale invoice.
 */
export function useCreateGrant(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((values) => apiRequest.post('sales/grants', values), {
    onSuccess: () => {
      // Invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Edits the given sale invoice.
 */
export function useEditGrant(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/grants/${id}`, values),
    {
      onSuccess: (res, [id, values]) => {
        // Invalidate specific grant.
        queryClient.invalidateQueries([t.GRANT, id]);

        // Invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

/**
 * Deletes the given sale invoice.
 */
export function useDeleteGrant(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.delete(`sales/grants/${id}`), {
    onSuccess: (res, id) => {
      // Invalidate specific grant.
      queryClient.invalidateQueries([t.GRANT, id]);

      // Invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

/**
 * Deletes the given sale invoice.
 */
export function useCloseGrant(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation((id) => apiRequest.post(`sales/grants/${id}/close`), {
    onSuccess: (res, id) => {
      queryClient.invalidateQueries([t.GRANT, id]);

      // Invalidate queries.
      commonInvalidateQueries(queryClient);
    },
    ...props,
  });
}

const transformGrants = (res) => ({
  grants: res.data.data,
  pagination: transformPagination(res.data.pagination),
  filterMeta: res.data.filter_meta,
});

/**
 * Retrieve sale invoices list with pagination meta.
 */
export function useGrants(query, props) {
  return useRequestQuery(
    ['GRANTS', query],
    { method: 'get', url: 'sales/grants', params: query },
    {
      select: transformGrants,
      defaultData: {
        grants: [],
        pagination: {
          page: 1,
          page_size: 12,
          total: 0,
        },
        filterMeta: {},
      },
      ...props,
    },
  );
}

/**
 * Retrieve sale invoices list with pagination meta.
 */
export function useGrant(id, props) {
  return useRequestQuery(
    ['GRANT', id],
    { method: 'get', url: `sales/grants/${id}` },
    {
      select: (res) => res.data.grant,
      defaultData: {},
      ...props,
    },
  );
}

/**
 * Retrieve the grant pdf document data.
 * @param {number} grantId -
 */
export function usePdfGrant(grantId: number) {
  return useRequestPdf({ url: `sales/grants/${grantId}` });
}

export function useRefreshGrants() {
  const queryClient = useQueryClient();

  return {
    refresh: () => {
      queryClient.invalidateQueries(t.GRANTS);
    },
  };
}

export function useCreateNotifyGrantBySMS(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();
  return useMutation(
    ([id, values]) =>
      apiRequest.post(`sales/grants/${id}/notify-by-sms`, values),
    {
      onSuccess: (res, [id, values]) => {
        queryClient.invalidateQueries([t.NOTIFY_GRANT_BY_SMS, id]);

        // Invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

export function useGrantSMSDetail(grantId, props, requestProps) {
  return useRequestQuery(
    [t.GRANT_SMS_DETAIL, grantId],
    {
      method: 'get',
      url: `sales/grants/${grantId}/sms-details`,
      ...requestProps,
    },
    {
      select: (res) => res.data.data,
      defaultData: {},
      ...props,
    },
  );
}

/**
 *
 */
export function useSendSaleGrantMail(props) {
  const queryClient = useQueryClient();
  const apiRequest = useApiRequest();

  return useMutation(
    ([id, values]) => apiRequest.post(`sales/grants/${id}/mail`, values),
    {
      onSuccess: () => {
        // Invalidate queries.
        commonInvalidateQueries(queryClient);
      },
      ...props,
    },
  );
}

export interface GetSaleGrantMailStateResponse {
  attachGrant: boolean;

  closedAtDate: string;
  closedAtDateFormatted: string;

  companyName: string;
  customerName: string;

  formatArgs: Record<string, any>;

  from: string[];
  fromOptions: Array<{ mail: string; label: string; primary: boolean; }>;
  message: string;

  grantDate: string;
  grantDateFormatted: string;

  subject: string;

  subtotal: number;
  subtotalFormatted: string;

  to: string[];
  toOptions: Array<{ mail: string; label: string; primary: boolean; }>;

  // # Discount
  discountAmount: number;
  discountAmountFormatted: string;
  discountLabel: string;
  discountPercentage: number | null;
  discountPercentageFormatted: string;

  // # Adjustment
  adjustment: number,
  adjustmentFormatted: string,

  // # Total
  total: number;
  totalFormatted: string;

  companyLogoUri?: string | null;
  primaryColor?: string | null;

  entries: Array<{
    name: string;
    quantity: number;
    quantityFormatted: string;
    rate: number;
    rateFormatted: string;
    total: number;
    totalFormatted: string
  }>,
  grantNumber: string;
}

export function useSaleGrantMailState(
  grantId: number,
  props?: UseQueryOptions<GetSaleGrantMailStateResponse, Error>,
): UseQueryResult<GetSaleGrantMailStateResponse, Error> {
  const apiRequest = useApiRequest();

  return useQuery<GetSaleGrantMailStateResponse, Error>(
    [t.GRANT_MAIL_OPTIONS, grantId],
    () =>
      apiRequest
        .get(`sales/grants/${grantId}/mail`)
        .then((res) => transformToCamelCase(res.data.data)),
  );
}

export interface IGetGrantStateResponse {
  defaultTemplateId: number;
}

export function useGetGrantState(
  options?: UseQueryOptions<IGetGrantStateResponse, Error>,
): UseQueryResult<IGetGrantStateResponse, Error> {
  const apiRequest = useApiRequest();

  return useQuery<IGetGrantStateResponse, Error>(
    ['GRANT_STATE'],
    () =>
      apiRequest
        .get(`/sales/grants/state`)
        .then((res) => transformToCamelCase(res.data?.data)),
    { ...options },
  );
}

interface GetGrantHtmlResponse {
  htmlContent: string;
}

/**
 * Retrieves the sale grant html content.
 * @param {number} grantId
 * @param {UseQueryOptions<string, Error>} options
 * @returns {UseQueryResult<GetGrantHtmlResponse, Error>}
 */
export const useGetSaleGrantHtml = (
  grantId: number,
  options?: UseQueryOptions<string, Error>,
): UseQueryResult<GetGrantHtmlResponse, Error> => {
  const apiRequest = useApiRequest();

  return useQuery<GetGrantHtmlResponse, Error>(
    ['GRANT_HTML', grantId],
    () =>
      apiRequest
        .get(`sales/grants/${grantId}`, {
          headers: {
            Accept: 'application/json+html',
          },
        })
        .then((res) => transformToCamelCase(res.data)),
    { ...options },
  );
};
